/**
 * @file useAttachments Hook
 * @description React Query hooks for investment attachment operations.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';

import {
  attachmentsService,
  type UploadAttachmentDto,
  type AttachmentsListResponse,
  type ExtractedTextsResponse,
} from '../services/attachments.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { InvestmentAttachmentMeta, AttachmentUpdateDto } from '@medsuite/models';

/**
 * Query key factory for attachments
 */
export const attachmentKeys = {
  all: ['attachments'] as const,
  lists: () => [...attachmentKeys.all, 'list'] as const,
  list: (investmentId: number) => [...attachmentKeys.lists(), investmentId] as const,
  extractedTexts: (investmentId: number) =>
    [...attachmentKeys.all, 'extractedTexts', investmentId] as const,
};

/**
 * Fetch all attachments for an investment
 */
export function useAttachments(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentAttachmentMeta[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentAttachmentMeta[], Error>({
    queryKey: attachmentKeys.list(investmentId),
    queryFn: async () => {
      const response = await attachmentsService.getAll(investmentId);
      // Extract data from success wrapper
      const responseData = unwrapResponse<AttachmentsListResponse>(response.data);
      return responseData.attachments || [];
    },
    enabled: investmentId > 0,
    ...options,
  });
}

/**
 * Fetch extracted texts for AI context
 */
export function useExtractedTexts(
  investmentId: number,
  options?: Omit<UseQueryOptions<ExtractedTextsResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<ExtractedTextsResponse, Error>({
    queryKey: attachmentKeys.extractedTexts(investmentId),
    queryFn: async () => {
      const response = await attachmentsService.getExtractedTexts(investmentId);
      // Extract data from success wrapper
      return unwrapResponse<ExtractedTextsResponse>(response.data);
    },
    enabled: investmentId > 0,
    ...options,
  });
}

/**
 * Upload a new attachment
 */
export function useUploadAttachment(
  investmentId: number,
  options?: UseMutationOptions<InvestmentAttachmentMeta, Error, UploadAttachmentDto>
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentAttachmentMeta, Error, UploadAttachmentDto>({
    mutationFn: async (data) => {
      const response = await attachmentsService.upload(investmentId, data);
      // Extract data from success wrapper
      return unwrapResponse<InvestmentAttachmentMeta>(response.data);
    },
    onSuccess: () => {
      // Invalidate attachments list
      queryClient.invalidateQueries({ queryKey: attachmentKeys.list(investmentId) });
      // Also invalidate extracted texts since new upload may have extracted text
      queryClient.invalidateQueries({ queryKey: attachmentKeys.extractedTexts(investmentId) });
    },
    ...options,
  });
}

/**
 * Update attachment metadata
 */
export function useUpdateAttachment(
  investmentId: number,
  options?: UseMutationOptions<
    InvestmentAttachmentMeta,
    Error,
    { attachmentId: number; data: AttachmentUpdateDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    InvestmentAttachmentMeta,
    Error,
    { attachmentId: number; data: AttachmentUpdateDto }
  >({
    mutationFn: async ({ attachmentId, data }) => {
      const response = await attachmentsService.update(investmentId, attachmentId, data);
      // Extract data from success wrapper
      return unwrapResponse<InvestmentAttachmentMeta>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attachmentKeys.list(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete an attachment
 */
export function useDeleteAttachment(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (attachmentId) => {
      await attachmentsService.delete(investmentId, attachmentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attachmentKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: attachmentKeys.extractedTexts(investmentId) });
      // Also invalidate investment summary in case it references attachments
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Download an attachment (returns blob URL)
 */
export function useDownloadAttachment(investmentId: number) {
  return useMutation<string, Error, { attachmentId: number; filename: string }>({
    mutationFn: async ({ attachmentId, filename }) => {
      const blob = await attachmentsService.download(investmentId, attachmentId);
      const url = URL.createObjectURL(blob);

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL after a short delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      return url;
    },
  });
}
